import type { BlockResponse } from '../api';
import type { PdfPageViewport } from '../hooks/usePdfRenderer';
import { blockColor, blockLabel } from './blockStyles';

const LABEL_FONT_SIZE = 10;
const LABEL_HEIGHT = 15;
const LABEL_CHAR_WIDTH = 6;
const LABEL_PAD_X = 5;
// Below these box dimensions the type tag would overwhelm the box, so we drop it (hover tooltip stays).
const MIN_BOX_W = 26;
const MIN_BOX_H = 16;

interface BlocksOverlayProps {
  blocks: ReadonlyArray<BlockResponse>;
  /** Absolute page number currently rendered by the viewer. */
  currentPage: number;
  /** Canvas viewport (CSS px) the block boxes are scaled against. */
  viewport: PdfPageViewport;
}

/**
 * SVG layer drawn over the PDF canvas that marks Mistral OCR 4 structural blocks (title, table,
 * signature, …) for the page in view, coloured by type. Each box carries a persistent type tag in
 * its top-left corner (Spanish label) so the structure is legible without hovering; the hover
 * tooltip still reveals the text snippet. Block coordinates are page-normalised ([0,1]) so they
 * scale to the canvas viewport. The layer is click-through; only the boxes capture hover.
 */
export function BlocksOverlay({ blocks, currentPage, viewport }: BlocksOverlayProps): JSX.Element | null {
  const pageBlocks = blocks.filter((b) => b.pageNumber === currentPage);
  if (pageBlocks.length === 0) {
    return null;
  }

  return (
    <svg
      className="absolute inset-0"
      width={viewport.width}
      height={viewport.height}
      style={{ pointerEvents: 'none' }}
      aria-hidden="true"
    >
      {pageBlocks.map((b, index) => {
        const color = blockColor(b.blockType);
        const label = blockLabel(b.blockType);
        const x = b.x * viewport.width;
        const y = b.y * viewport.height;
        const w = b.width * viewport.width;
        const h = b.height * viewport.height;
        const showLabel = w >= MIN_BOX_W && h >= MIN_BOX_H;
        const labelWidth = label.length * LABEL_CHAR_WIDTH + LABEL_PAD_X * 2;
        return (
          <g key={index}>
            <rect
              x={x}
              y={y}
              width={w}
              height={h}
              fill={color}
              fillOpacity={0.07}
              stroke={color}
              strokeWidth={1.5}
              strokeOpacity={0.85}
              rx={2}
              style={{ pointerEvents: 'auto' }}
            >
              <title>{b.content ? `${label} — ${b.content}` : label}</title>
            </rect>
            {showLabel ? (
              <g style={{ pointerEvents: 'none' }}>
                <rect x={x} y={y} width={labelWidth} height={LABEL_HEIGHT} fill={color} rx={2} />
                <text
                  x={x + LABEL_PAD_X}
                  y={y + LABEL_HEIGHT - 4}
                  fill="#ffffff"
                  fontSize={LABEL_FONT_SIZE}
                  fontWeight={600}
                  style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}
                >
                  {label}
                </text>
              </g>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}
